// Illustrated voucher cards (Holidays, Diwali, Birthday, New baby), ported from the design's
// collection engine: the print under a light streak, the picture's own glows and twinkles.
// Pure data: shader sources + per-card light config.

import type { IllustratedFinish } from 'content/gifting/voucherConfig';

export const ILLUS_VS =
  'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';

export const ILLUS_FS = `precision highp float;
uniform vec2 uRes;uniform float uDpr;uniform sampler2D uImg;uniform vec4 uCrop;uniform vec2 uTilt;uniform float uHolo,uSw,uStr,uFloor,uT,uImgA,uSway,uGam;uniform vec2 uPtr;uniform vec3 uLight;
uniform vec4 uG[3];uniform vec4 uGC[3];uniform vec4 uSp;uniform vec3 uSpC;
float sdRB(vec2 p,vec2 b,float r){vec2 q=abs(p)-b+r;return length(max(q,0.))+min(max(q.x,q.y),0.)-r;}
float prof(float x,float w){return .66*exp(-pow(x/w,2.))+.26*exp(-pow(x/(w*2.6),2.))+.08*exp(-pow(x/(w*5.),2.));}
float hash(vec2 p){return fract(sin(dot(p,vec2(12.9898,78.233)))*43758.5453);}
vec3 screen(vec3 a,vec3 b){return 1.-(1.-a)*(1.-b);}
void main(){
 vec2 fc=vec2(gl_FragCoord.x,uRes.y-gl_FragCoord.y);vec2 uv=fc/uRes;vec2 p=(uv-.5)*vec2(uRes.x/uRes.y,1.);
 /* the print sits slightly below the surface: it drifts against the tilt */
 vec2 tc=uCrop.xy+(uv-.5)*uCrop.zw-uTilt*vec2(.005,.005);
 /* breeze: shadow areas sway a little (leaves, balloons, pine) */
 if(uSway>0.){float sl=dot(texture2D(uImg,tc).rgb,vec3(.299,.587,.114));float m=smoothstep(.78,.45,sl);
  tc+=vec2(sin(uT*.9+tc.y*7.+tc.x*3.),cos(uT*.7+tc.x*6.))*uSway*m*(1.+.5*uHolo);}
 vec3 img=pow(texture2D(uImg,tc).rgb,vec3(uGam));
 float lum=dot(img,vec3(.299,.587,.114));
 /* light streak geometry, same as the metal cards */
 float ang=radians(-27.+uTilt.x*9.-uTilt.y*5.);vec2 dir=vec2(cos(ang),sin(ang)),nrm=vec2(-dir.y,dir.x);
 float q=dot(p,nrm),along=dot(p,dir);
 float qb=-.02+uTilt.x*.42+uTilt.y*.14+uSw;
 float s1=smoothstep(-.75,qb,q),s2=smoothstep(qb+.02,.62,q);
 vec3 col=img*(.9+.13*s1-.12*s2);
 float refl=mix(uFloor,1.,smoothstep(.15,.9,lum));
 float w=.095*(1.+.4*uHolo),dsp=.026;
 float bR=prof(q-qb-dsp,w),bG=prof(q-qb,w),bB=prof(q-qb+dsp,w);
 float md=.8+.2*sin(along*3.1+.6);
 col+=vec3(bR,bG*.92,bB)*uStr*md*refl*uLight;
 float glow=exp(-pow((q-qb)/(w*3.8*(1.+.45*uHolo)),2.));col+=glow*uStr*.25*uLight*refl;
 float warm=smoothstep(.06,.3,img.r-img.b)*smoothstep(.42,.8,lum);
 col+=warm*vec3(1.,.84,.5)*(bG*1.5+glow*.35)*(.55+.45*uHolo);
 /* the picture's own light sources: flames, tea, sunlight, gold */
 vec3 add=vec3(0.);
 for(int i=0;i<3;i++){vec4 g=uG[i];vec4 gc=uGC[i];if(g.w<=0.)continue;float fi=float(i);
  float fl=1.-gc.w*(.22+.18*sin(uT*9.1+fi*2.3)*sin(uT*5.7+fi)+.08*sin(uT*23.+fi*4.));
  float br=1.+(1.-gc.w)*.18*sin(uT*.6+fi);
  vec2 dd=(tc-g.xy+vec2(0.,gc.w*.004*sin(uT*7.+fi)))*vec2(uImgA,1.);float rr=g.z*(1.+gc.w*.05*sin(uT*11.+fi*3.));
  float e=exp(-dot(dd,dd)/(rr*rr)),core=exp(-dot(dd,dd)/(rr*rr*.05)),halo=exp(-dot(dd,dd)/(rr*rr*5.));
  add+=gc.rgb*(e*.5+core*.45+halo*.12)*g.w*fl*br*(1.+.7*uHolo);}
 col=screen(col,add);
 /* twinkles on gold details or bright specks */
 if(uSp.y>0.){vec2 cid=floor(fc/(2.6*uDpr));float h=hash(cid);
  float msk=uSp.x<.5?warm*1.4:smoothstep(.5,.78,lum)*smoothstep(.5,.2,abs(img.g-img.b)*0.+lum-.3);
  float tw=step(1.-.035*uSp.y,h)*pow(.5+.5*sin(uT*(2.+h*3.)+h*60.),12.);
  col+=uSpC*tw*clamp(msk,0.,1.)*(.7+1.1*uHolo);}
 float f=fract((fc.x+fc.y)*.70711/(2.*uDpr));float ridge=smoothstep(0.,.28,f)*(1.-smoothstep(.3,.58,f));
 col+=uLight*.03*ridge*(.3+.9*clamp(bG*1.1+glow*.9,0.,1.))*refl;
 vec2 pp=(uPtr-.5)*vec2(uRes.x/uRes.y,1.);col+=uHolo*exp(-dot(p-pp,p-pp)*8.)*.1*refl*uLight;
 if(uHolo>.001){float ph=(q-qb)*1.5+along*.35+uTilt.x*.9+uTilt.y*.5;vec3 hue=.5+.5*cos(6.2832*(ph+vec3(0.,.33,.67)));
  float zone=exp(-pow((q-qb)/(.45+.75*uHolo),2.))*.9;col=mix(col,col*.7+hue*.5*mix(vec3(1.),uLight,.4),uHolo*zone*.1*refl);}
 vec2 px=fc-uRes*.5,hs=uRes*.5;float r=uRes.x*.035,d=sdRB(px,hs,r);vec2 e=vec2(1.,0.);
 vec2 n=normalize(vec2(sdRB(px+e,hs,r)-sdRB(px-e,hs,r),sdRB(px+e.yx,hs,r)-sdRB(px-e.yx,hs,r))+1e-5);
 float lit=clamp(dot(n,normalize(vec2(-1.-uTilt.x*1.1,-1.-uTilt.y*1.1)))*.5+.5,0.,1.);
 float edge=smoothstep(-1.5*uDpr,-1.*uDpr,d)*(1.-smoothstep(-.4*uDpr,.2*uDpr,d));
 col+=edge*pow(lit,1.6)*uLight*.75;col=mix(col,col*.62,edge*(1.-lit)*.7);
 float g=(hash(fc)*.78+hash(floor(fc/uDpr)+17.3)*.22)-.5;col*=1.+g*.035;
 col+=(hash(fc+3.1)-.5)/255.;
 gl_FragColor=vec4(clamp(col,0.,1.),1.);
}`;

export type IllusConfig = {
  light: readonly [number, number, number];
  str: number;
  floor: number;
  gam?: number;
  cx?: number;
  cy?: number;
  sway: number;
  /** [mode (0 warm details, 1 bright specks), amount, r, g, b] */
  spark: readonly [number, number, number, number, number];
  /** [x, y, radius, strength, r, g, b, flicker] in image coordinates */
  glow: readonly (readonly number[])[];
};

export const ILLUS_CONFIG: Record<IllustratedFinish, IllusConfig> = {
  birthday: {
    light: [1, 0.97, 0.92],
    str: 0.09,
    floor: 0.15,
    gam: 1.22,
    cy: 0.462,
    sway: 0.0018,
    spark: [0, 0.6, 1, 0.86, 0.5],
    glow: [
      [0.112, 0.058, 0.045, 0.5, 1, 0.74, 0.4, 1],
      [0.785, 0.715, 0.04, 0.18, 1, 0.84, 0.48, 0.15],
    ],
  },
  baby: {
    light: [1, 0.97, 0.9],
    str: 0.07,
    floor: 0.15,
    gam: 1.2,
    sway: 0.0024,
    spark: [0, 0.25, 1, 0.85, 0.55],
    glow: [
      [0.137, 0.17, 0.06, 0.38, 1, 0.78, 0.4, 0.25],
      [0.783, 0.708, 0.02, 0.4, 1, 0.88, 0.58, 0.1],
    ],
  },
  diwali: {
    light: [1, 0.86, 0.62],
    str: 0.3,
    floor: 0.5,
    sway: 0,
    spark: [0, 1.4, 1, 0.8, 0.4],
    glow: [
      [0.268, 0.308, 0.13, 0.6, 1, 0.72, 0.36, 1],
      [0.727, 0.582, 0.085, 0.55, 1, 0.72, 0.36, 1],
      [0.86, 0.78, 0.075, 0.55, 1, 0.72, 0.36, 1],
    ],
  },
  holidays: {
    light: [1, 0.93, 0.8],
    str: 0.28,
    floor: 0.5,
    sway: 0.0018,
    spark: [1, 1.2, 0.9, 0.95, 1],
    glow: [
      [0.905, 0.855, 0.035, 1.05, 1, 0.86, 0.5, 0.2],
      [0.125, 0.09, 0.11, 0.4, 1, 0.78, 0.55, 0.35],
      [0.88, 0.83, 0.18, 0.22, 1, 0.7, 0.45, 0.5],
    ],
  },
};
